import React, { useState, useEffect } from 'react';
import { Upload, Save, X, Building2, Sparkles, Image as ImageIcon, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Slider } from '@shared/components/ui/slider';
import { useToast } from '@shared/components/ui/use-toast';
import { supabase } from '@shared/supabaseClient';

/**
 * System Settings Page
 * Configure company branding, depot name, and system preferences
 */
const SystemSettings = () => {
  const { toast } = useToast();
  const [depotName, setDepotName] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoSize, setLogoSize] = useState(80);
  const [uploading, setUploading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [brandingOpen, setBrandingOpen] = useState(true);
  const [loginBgOpen, setLoginBgOpen] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    // Load depot name from localStorage
    const savedDepotName = localStorage.getItem('depotName') || 'Depot Induction System';
    setDepotName(savedDepotName);

    // Load existing logo from localStorage so user can see current state
    const savedLogoUrl = localStorage.getItem('company_logo_url');
    const savedLogoSize = parseInt(localStorage.getItem('company_logo_size') || '80');
    
    setLogoUrl(savedLogoUrl || null);
    setLogoSize(savedLogoSize);
    setLogoFile(null);
    setHasChanges(false);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Invalid File',
          description: 'Please upload an image file (PNG, JPG, SVG)',
          variant: 'destructive',
        });
        return;
      }

      // Validate file size (max 5MB for Supabase Storage)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File Too Large',
          description: 'Please upload an image smaller than 5MB',
          variant: 'destructive',
        });
        return;
      }

      setLogoFile(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setLogoUrl(previewUrl);
      setHasChanges(true);
    }
  };

  const handlePortalLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Invalid File',
          description: 'Please upload an image file (PNG, JPG, SVG)',
          variant: 'destructive',
        });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File Too Large',
          description: 'Please upload an image smaller than 5MB',
          variant: 'destructive',
        });
        return;
      }

      setPortalLogoFile(file);
      const previewUrl = URL.createObjectURL(file);
      setPortalLogoUrl(previewUrl);
      setHasChanges(true);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      // Remove from Supabase Storage
      const savedLogoPath = localStorage.getItem('companyLogoPath');
      if (savedLogoPath && savedLogoPath.trim() && savedLogoPath !== 'null' && savedLogoPath !== 'undefined') {
        await supabase.storage
          .from('company-assets')
          .remove([savedLogoPath]);
        
        localStorage.removeItem('companyLogoPath');
      }
      
      localStorage.removeItem('company_logo_url');
      localStorage.removeItem('company_logo_size');
      
      setLogoUrl(null);
      setLogoFile(null);
      setHasChanges(true);
      
      toast({
        title: 'Logo Removed',
        description: 'Company logo has been removed',
      });

      window.dispatchEvent(new Event('settingsUpdated'));
    } catch (error) {
      console.error('Error removing logo:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove logo',
        variant: 'destructive',
      });
    }
  };

  const handleSaveSettings = async () => {
    try {
      setUploading(true);

      // Save depot name to localStorage
      localStorage.setItem('depotName', depotName);
      
      // Upload logo to Supabase Storage if a new file was selected
      if (logoFile) {
        // Delete old logo if exists
        const oldLogoPath = localStorage.getItem('companyLogoPath');
        if (oldLogoPath && oldLogoPath.trim() && oldLogoPath !== 'null' && oldLogoPath !== 'undefined') {
          await supabase.storage
            .from('company-assets')
            .remove([oldLogoPath]);
        }

        // Upload new logo
        const fileExt = logoFile.name.split('.').pop();
        const fileName = `logo-${Date.now()}.${fileExt}`;
        const filePath = `logos/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('company-assets')
          .upload(filePath, logoFile, {
            cacheControl: '3600',
            upsert: true
          });

        if (uploadError) {
          throw uploadError;
        }

        // Save logo path to localStorage
        localStorage.setItem('companyLogoPath', filePath);

        // Get public URL
        const { data } = supabase.storage
          .from('company-assets')
          .getPublicUrl(filePath);
        
        // Persist branding for header/login fallback
        const publicUrl = data?.publicUrl || null;
        if (publicUrl) {
          localStorage.setItem('company_logo_url', publicUrl);
        }

        setLogoUrl(publicUrl);
      }

      // Always save logo size
      localStorage.setItem('company_logo_size', String(logoSize));

      setHasChanges(false);
      setLogoFile(null);

      toast({
        title: 'Settings Saved',
        description: 'Settings saved successfully.',
      });

      // Trigger header update without full page reload
      window.dispatchEvent(new Event('settingsUpdated'));
    } catch (error) {
      console.error('Save settings error:', error);
      toast({
        title: 'Save Failed',
        description: error.message || 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-6 space-y-6" style={{ paddingTop: '24px' }}>
      <div>
        <h1 className="text-3xl font-bold">System Settings</h1>
        <p className="text-muted-foreground mt-1">
          Configure system-wide settings and branding
        </p>
      </div>

      {/* Company Branding */}
      <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => setBrandingOpen(!brandingOpen)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-500" />
              <div>
                <CardTitle>Company Branding</CardTitle>
                <CardDescription>
                  Customise your organisation's logo and visual identity
                </CardDescription>
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${brandingOpen ? 'rotate-180' : ''}`} />
          </div>
        </CardHeader>
        {brandingOpen && (
            <CardContent className="space-y-8">
              {/* Company Logo Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-yellow-500" />
                  <div>
                    <h3 className="text-lg font-semibold">Company Logo</h3>
                    <p className="text-sm text-muted-foreground">Upload your company logo to display in the header, login page, and Supplier Portal</p>
                  </div>
                </div>

                {/* Logo Preview */}
                <div>
                  <Label className="text-sm font-medium">Logo Preview</Label>
                  <div 
                    className="mt-2 w-full max-w-md h-32 rounded-lg border-2 border-dashed border-border flex items-center justify-center overflow-hidden mx-auto"
                    style={{ backgroundColor: 'hsl(0 0% 12%)' }}
                  >
                    {logoUrl ? (
                      <img 
                        src={logoUrl} 
                        alt="Company Logo Preview" 
                        style={{ height: `${logoSize}px`, width: 'auto', objectFit: 'contain' }}
                      />
                    ) : (
                      <div className="text-center">
                        <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">No logo uploaded</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Logo Size Slider */}
                <div className="space-y-2 max-w-md">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-yellow-500">Logo Size</Label>
                    <span className="text-sm text-muted-foreground">{logoSize}px</span>
                  </div>
                  <Slider
                    value={[logoSize]}
                    onValueChange={(val) => {
                      setLogoSize(val[0]);
                      setHasChanges(true);
                    }}
                    min={40}
                    max={500}
                    step={2}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Adjust the height of your logo in the header (40-500px)
                  </p>
                </div>

                {/* Upload Logo */}
                <div className="space-y-2">
                  <Label className="font-medium">Upload Logo</Label>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => document.getElementById('logo-upload').click()}
                      disabled={uploading}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Choose File
                    </Button>
                    {logoUrl && (
                      <Button
                        variant="ghost"
                        className="text-red-400 hover:text-red-300"
                        onClick={handleRemoveLogo}
                        disabled={uploading}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Remove Logo (Use Default)
                      </Button>
                    )}
                  </div>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <p className="text-xs text-muted-foreground">
                    PNG, JPG, SVG or WebP • Max 2MB • Recommended 128px height
                  </p>
                </div>

                {/* Logo Guidelines */}
                <div className="rounded-lg border border-border p-4 bg-muted/30 max-w-md">
                  <div className="flex items-center gap-2 text-green-400 mb-2">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-medium">Logo Guidelines</span>
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-1 ml-6 list-disc">
                    <li>Recommended height: 64–128px (width auto-scales)</li>
                    <li>Transparent background works best on dark themes</li>
                    <li>Logo should be legible on dark backgrounds</li>
                    <li>Horizontal/landscape logos work best in the header</li>
                    <li>This logo is automatically shared with the Supplier Portal</li>
                  </ul>
                </div>
              </div>

              {/* Portal Logo Info */}
              <div className="border-t border-border" />
              <div className="rounded-lg border border-sky-500/30 bg-sky-500/10 p-4">
                <div className="flex items-center gap-2 text-sky-400 mb-2">
                  <ImageIcon className="w-4 h-4" />
                  <span className="text-sm font-medium">Supplier Portal</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  The Supplier Portal automatically uses your company logo above. When you update it here, the portal login page and sidebar will reflect the change instantly.
                </p>
              </div>
            </CardContent>
        )}
      </Card>

      {/* Save Button */}
      {hasChanges && (
        <div className="flex justify-end">
          <Button onClick={handleSaveSettings} size="lg" disabled={uploading}>
            <Save className="w-4 h-4 mr-2" />
            {uploading ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SystemSettings;
